/**
 * FeedbackModal (#504, D4-FBK-001/010).
 *
 * Dead-simple feedback flow for non-technical realtors:
 *  1. Pick one of four friendly type tiles (Bug / Data integrity / Suggestion /
 *     Feature).
 *  2. Write a description (the issue title is derived from it server-side, #1052).
 *  3. Optionally attach a screenshot/video OR record the screen in-app
 *     (getDisplayMedia + MediaRecorder).
 *  4. Submit -> a confirmation ("Thanks -- logged as Report NNN") -- the CRM
 *     report number the reports list shows and searches (#4507).
 *
 * No GitHub wording is shown. The browser sends only non-authoritative client
 * hints; the server derives identity/role/agency/instance and raises the issue.
 *
 * Accessibility + chrome (role=dialog, focus-trap, Escape, bottom-sheet on
 * mobile, body-scroll lock) come from the shared <Modal> primitive (#607).
 */
import { useEffect, useRef, useState } from "react";
import {
  Bug,
  Calculator,
  CheckCircle2,
  Circle,
  Lightbulb,
  Loader2,
  Paperclip,
  Send,
  Sparkles,
  Square,
  Video,
} from "lucide-react";
import FileDropzone from "../documents/FileDropzone";
import Modal from "../ui/Modal";
import {
  captureClientHints,
  submitFeedback,
  type FeedbackType,
} from "../../api/feedback";
import type { BrightmarkAnnotation } from "../../lib/brightmark/types";
import { AnnotationChip } from "./annotate/AnnotationChip";
import { VoiceNoteButton, VoiceNoteStatus } from "../voice/VoiceNoteButton";
import { insertAtCursor, useVoiceNote } from "../voice/useVoiceNote";

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Optional brightmark annotation captured in annotate mode (#546). When present the modal shows a
   * non-editable annotation chip (with a thumbnail of `initialMedia`) and submits the annotation
   * alongside the report. Discarding it falls back to a classic submission.
   */
  annotation?: BrightmarkAnnotation | null;
  /** Pre-selected feedback type (annotate mode pre-selects "bug"). */
  initialType?: FeedbackType;
  /** Pre-attached media — the element screenshot from annotate mode (rides the existing media slot). */
  initialMedia?: File | Blob | null;
  /** Filename to show for `initialMedia`. */
  initialMediaName?: string;
  /** Called when the user discards the captured annotation (drops both annotation + auto screenshot). */
  onDiscardAnnotation?: () => void;
  /** Pre-filled description, e.g. from the help assistant (#3604). The user can edit it. */
  initialDescription?: string;
}

interface TypeTile {
  type: FeedbackType;
  label: string;
  blurb: string;
  icon: typeof Bug;
}

const TYPE_TILES: TypeTile[] = [
  { type: "bug", label: "Something's broken", blurb: "A bug or error", icon: Bug },
  {
    type: "data_integrity",
    label: "Numbers look wrong",
    blurb: "A figure or total seems off",
    icon: Calculator,
  },
  { type: "suggestion", label: "I have an idea", blurb: "A suggestion", icon: Lightbulb },
  { type: "feature", label: "I'd like a new feature", blurb: "Request something new", icon: Sparkles },
];

const DESCRIPTION_MAX = 5000;

const APP_VERSION =
  (import.meta.env.VITE_APP_VERSION as string | undefined) || "unknown";

export function FeedbackModal({
  open,
  onClose,
  annotation = null,
  initialType = "bug",
  initialMedia = null,
  initialMediaName = "element-screenshot.png",
  onDiscardAnnotation,
  initialDescription = "",
}: FeedbackModalProps) {
  const [type, setType] = useState<FeedbackType>("bug");
  const [description, setDescription] = useState("");
  const [media, setMedia] = useState<File | Blob | null>(null);
  const [mediaName, setMediaName] = useState("");
  const [recording, setRecording] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doneRef, setDoneRef] = useState<number | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Voice note (#3829): the transcript is inserted at the cursor for review/edit.
  const descRef = useRef<HTMLTextAreaElement>(null);
  const descValueRef = useRef(description);
  descValueRef.current = description;
  const voice = useVoiceNote((text) => {
    const el = descRef.current;
    const next = insertAtCursor(el, descValueRef.current, text, DESCRIPTION_MAX);
    setDescription(next.value);
    window.setTimeout(() => {
      el?.focus();
      el?.setSelectionRange?.(next.caret, next.caret);
    }, 0);
  });

  // The modal hides rather than unmounts, so a voice note must be abandoned on
  // close: mic released, upload aborted, nothing inserted into the hidden form.
  const { cancel: cancelVoice, clearError: clearVoiceError } = voice;
  useEffect(() => {
    if (!open) cancelVoice();
  }, [open, cancelVoice]);

  // Reset all local state when the modal opens.
  useEffect(() => {
    if (!open) return;
    setType(initialType);
    setDescription(initialDescription);
    setMedia(initialMedia ?? null);
    setMediaName(initialMedia ? initialMediaName : "");
    setError(null);
    setDoneRef(null);
    setSubmitting(false);
    clearVoiceError();
    // initialMedia/initialType/initialMediaName are captured once at open; we intentionally re-seed
    // only when the modal (re)opens, so the user's edits during a session are not clobbered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When annotate mode delivers the screenshot after the modal is already open (snapdom is async),
  // attach it to the media slot if the user has not picked their own.
  useEffect(() => {
    if (!open) return;
    if (initialMedia && !media) {
      setMedia(initialMedia);
      setMediaName(initialMediaName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMedia, open]);

  // Stop any in-flight recording if the modal closes.
  useEffect(() => {
    if (!open && recorderRef.current && recording) {
      recorderRef.current.stop();
      setRecording(false);
    }
  }, [open, recording]);

  // Build a thumbnail object URL for the captured element screenshot (image media only). Revoked on
  // change/unmount to avoid leaks. Only relevant while an annotation is attached.
  useEffect(() => {
    if (!annotation || !media) {
      setThumbnailUrl(null);
      return;
    }
    const type = (media as File).type || "";
    if (!type.startsWith("image/")) {
      setThumbnailUrl(null);
      return;
    }
    const url = URL.createObjectURL(media);
    setThumbnailUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [annotation, media]);

  if (!open) return null;

  function discardAnnotation() {
    // Drop both the annotation and the auto-attached screenshot, returning to the classic flow.
    if (media === initialMedia) {
      setMedia(null);
      setMediaName("");
    }
    onDiscardAnnotation?.();
  }

  function onPickFile(files: File[]) {
    const f = files[0];
    if (!f) return;
    setMedia(f);
    setMediaName(f.name);
    setError(null);
  }

  async function startRecording() {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
      });
      chunksRef.current = [];
      const rec = new MediaRecorder(stream, { mimeType: "video/webm" });
      rec.ondataavailable = (ev) => {
        if (ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        setMedia(blob);
        setMediaName("screen-recording.webm");
        setRecording(false);
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      setError("Screen recording was cancelled or is not available.");
    }
  }

  function stopRecording() {
    recorderRef.current?.stop();
  }

  async function handleSubmit() {
    // The Title field was removed (#1052); the server derives the issue/record
    // title from the description, so the description is the primary required
    // field here.
    if (!description.trim()) {
      setError("Please describe what happened.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await submitFeedback({
        type,
        description: description.trim(),
        route: window.location.pathname,
        app_version: APP_VERSION,
        local_time: new Date().toString(),
        client_hints: captureClientHints(),
        media,
        annotation,
      });
      setDoneRef(result.reference);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Could not send your feedback. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Send feedback"
      // The feedback report dialog must not offer its own in-modal feedback trigger (#628) —
      // that would be recursive. Suppress it here.
      feedbackTrigger={false}
      // Pin the header + footer and let the BODY scroll (#1680). Without this the
      // whole panel scrolls and the Submit/Cancel footer rides inside the scroll
      // region; on the mobile bottom-sheet the tallest variant (the bug report,
      // which also shows the annotation chip + media controls) pushed the footer
      // past the viewport, leaving Submit unreachable. bodyScroll keeps Submit
      // visible regardless of which category is selected.
      bodyScroll
      footer={
        doneRef !== null ? undefined : (
          <>
            <button
              onClick={onClose}
              className="min-h-[44px] px-4 rounded-md border border-[var(--color-border)] text-sm font-medium text-[var(--color-text-secondary)]"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || voice.phase !== "idle"}
              className="min-h-[44px] px-4 rounded-md bg-[var(--color-accent)] text-white text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Send feedback
            </button>
          </>
        )
      }
    >
      {doneRef !== null ? (
        /* Success confirmation */
        <div className="p-6 sm:p-8 flex flex-col items-center text-center gap-3">
          <CheckCircle2 className="h-12 w-12 text-[var(--success)]" />
          <p className="text-lg font-semibold text-[var(--color-text-primary)]">
            Thanks -- logged as Report {doneRef}.
          </p>
          <p className="text-sm text-[var(--color-text-secondary)]">
            Our team will take a look. To follow it up, search for {doneRef} on
            the Feedback reports page. You can close this window.
          </p>
          <button
            onClick={onClose}
            className="mt-2 min-h-[44px] px-5 rounded-md bg-[var(--color-accent)] text-white text-sm font-medium"
          >
            Done
          </button>
        </div>
      ) : (
        <div className="p-4 sm:p-5 space-y-4">
          {annotation && (
            <AnnotationChip
              annotation={annotation}
              thumbnailUrl={thumbnailUrl}
              onDiscard={discardAnnotation}
            />
          )}
          {/* Type tiles */}
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-2">
              What's this about?
            </label>
            <div className="grid grid-cols-2 gap-2">
              {TYPE_TILES.map((t) => {
                const Icon = t.icon;
                const active = type === t.type;
                return (
                  <button
                    key={t.type}
                    type="button"
                    onClick={() => setType(t.type)}
                    aria-pressed={active}
                    className={`flex flex-col items-start gap-1 min-h-[44px] rounded-lg border p-3 text-left transition-colors ${
                      active
                        ? "border-[var(--color-accent)] bg-[var(--color-accent)]/10"
                        : "border-[var(--color-border)] hover:bg-[var(--color-bg-page)]"
                    }`}
                  >
                    <span className="flex items-center gap-2 text-sm font-medium text-[var(--color-text-primary)]">
                      <Icon className="h-4 w-4 text-[var(--color-accent)]" />
                      {t.label}
                    </span>
                    <span className="text-xs text-[var(--color-text-muted)]">
                      {t.blurb}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Description (primary required field; the title is derived from it, #1052) */}
          <div>
            <div className="flex items-end justify-between gap-2 mb-1.5">
              <label
                htmlFor="fb-desc"
                className="block text-sm font-medium text-[var(--color-text-primary)]"
              >
                Tell us more
              </label>
              <VoiceNoteButton voice={voice} />
            </div>
            <textarea
              id="fb-desc"
              ref={descRef}
              value={description}
              maxLength={DESCRIPTION_MAX}
              onChange={(e) => setDescription(e.target.value)}
              // Taller when pre-filled (e.g. a help-chat summary + transcript,
              // #3605) so the user can review what will be sent.
              rows={initialDescription ? 8 : 4}
              placeholder="What happened, and what did you expect?"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 py-2 text-base text-[var(--color-text-primary)]"
            />
            <VoiceNoteStatus voice={voice} />
          </div>

          {/* Media attach + screen recording */}
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-1.5">
              Add a screenshot or recording (optional)
            </label>
            <div className="flex flex-wrap gap-2">
              <FileDropzone
                variant="inline"
                onFiles={onPickFile}
                accept="image/png,image/jpeg,image/webp,video/webm,video/mp4"
                aria-label="Attach a screenshot or recording"
              >
                <button
                  type="button"
                  className="inline-flex items-center gap-2 min-h-[44px] px-3 rounded-md border border-[var(--color-border)] text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-page)]"
                >
                  <Paperclip className="h-4 w-4" />
                  Attach file
                </button>
              </FileDropzone>
              {!recording ? (
                <button
                  type="button"
                  onClick={startRecording}
                  className="inline-flex items-center gap-2 min-h-[44px] px-3 rounded-md border border-[var(--color-border)] text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-page)]"
                >
                  <Video className="h-4 w-4" />
                  Record screen
                </button>
              ) : (
                <button
                  type="button"
                  onClick={stopRecording}
                  className="inline-flex items-center gap-2 min-h-[44px] px-3 rounded-md border border-red-400 text-sm text-red-600 dark:text-red-400"
                >
                  <Square className="h-4 w-4 fill-current" />
                  Stop recording
                </button>
              )}
            </div>
            {mediaName && (
              <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)]">
                {media && (media as File).type?.startsWith("video") ? (
                  <Video className="h-3.5 w-3.5" />
                ) : (
                  <Circle className="h-3.5 w-3.5" />
                )}
                {mediaName}
              </p>
            )}
            {recording && (
              <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                Recording your screen...
              </p>
            )}
          </div>

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>
      )}
    </Modal>
  );
}
